import { api } from '@/api';
import { createEntityAttachmentApi } from '@/api/common';
import type {
  FirmwarePaginatedResponse,
  FirmwareQueryParams,
  FirmwareCountResponse,
  FirmwareCountParams,
  CreateFirmwareRequest,
  CreateFirmwareResponse,
  UpdateFirmwareVersionRequest,
  CheckVersionExistsParams,
  DeleteFirmwaresRequest,
} from '../types';

// firmwareAttachments exposes the shared entity-attachment endpoints for one firmware. Reused by
// the upload hook to run create + enable against the nested deviceModels/firmwares owner path.
export function firmwareAttachments(modelId: number, firmwareUuid: string) {
  return createEntityAttachmentApi(`/v1/deviceModels/${modelId}/firmwares/${firmwareUuid}`);
}

export async function fetchFirmwares(
  modelId: number,
  params: FirmwareQueryParams
): Promise<FirmwarePaginatedResponse> {
  return api.get<FirmwarePaginatedResponse>(`/v1/deviceModels/${modelId}/firmwares`, {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      firmwareTypeIds: joinFirmwareTypeIds(params.firmwareTypeIds),
      countryId: params.countryId ?? undefined,
    },
  });
}

export async function fetchFirmwareCount(
  modelId: number,
  params: FirmwareCountParams
): Promise<FirmwareCountResponse> {
  return api.get<FirmwareCountResponse>(`/v1/deviceModels/${modelId}/firmwares/count`, {
    params: {
      firmwareTypeIds: joinFirmwareTypeIds(params.firmwareTypeIds),
      countryId: params.countryId ?? undefined,
    },
  });
}

// Encodes the selected firmware type ids as a comma-separated string for the backend,
// or undefined when no type filter is active so all types are returned.
function joinFirmwareTypeIds(firmwareTypeIds?: number[]): string | undefined {
  return firmwareTypeIds && firmwareTypeIds.length > 0 ? firmwareTypeIds.join(',') : undefined;
}

export async function createFirmware(
  request: CreateFirmwareRequest
): Promise<CreateFirmwareResponse> {
  return api.post<CreateFirmwareResponse>(
    `/v1/deviceModels/${request.modelId}/firmwares`,
    {
      firmwareTypeId: request.firmwareTypeId,
      countryId: request.countryId,
      versionName: request.versionName,
      firmwareName: request.firmwareName,
    }
  );
}

export async function checkVersionExists(
  params: CheckVersionExistsParams
): Promise<boolean> {
  return api.get<boolean>(`/v1/deviceModels/${params.modelId}/firmwares/check-version`, {
    params: {
      firmwareTypeId: params.firmwareTypeId,
      countryId: params.countryId ?? undefined,
      versionName: params.versionName,
    },
  });
}

// Updates the version string and/or name of an existing (draft) firmware.
export async function updateFirmwareVersion(
  request: UpdateFirmwareVersionRequest
): Promise<void> {
  return api.patch<void>(
    `/v1/deviceModels/${request.modelId}/firmwares/${request.firmwareId}`,
    {
      versionName: request.versionName,
      firmwareName: request.firmwareName,
    }
  );
}

export async function releaseFirmware(
  modelId: number,
  firmwareId: string
): Promise<void> {
  return api.put<void>(`/v1/deviceModels/${modelId}/firmwares/${firmwareId}/release`);
}

export async function deleteFirmwares(
  request: DeleteFirmwaresRequest
): Promise<void> {
  return api.delete<void>(`/v1/deviceModels/${request.modelId}/firmwares`, {
    ids: request.firmwareIds,
  });
}
